// client/src/pages/AdminUsers.js
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import axios from "axios";
import API_BASE_URL from '../config';
import { resolveAssetUrl } from '../utils/resolveUrl';
import toast from 'react-hot-toast';
import { hasPermission } from '../services/authService';
import { useCurrency } from '../context/CurrencyContext';
import './Admin.css';

// Helper to get JWT token from localStorage
const getToken = () => localStorage.getItem("token");

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newUser, setNewUser] = useState({ name: "", surname: "", email: "", password: "", role: "user", username: '', phoneNumber: '', country: '', stateProvince: '', city: '', zip: '', streetAddress: '', dateOfBirth: '', gender: '', marketingEmail: false, marketingSMS: false, preferredCurrency: 'ALL' });
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", surname: "", email: "", role: "user", password: "" });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [roleEditIndex, setRoleEditIndex] = useState(-1);
  const [roleEditValue, setRoleEditValue] = useState('');
  const [roleEditPerms, setRoleEditPerms] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePerms, setNewRolePerms] = useState([]);
  const canManageRoles = hasPermission('admin.roles');
  const [ordersByUser, setOrdersByUser] = useState({});
  const [ordersLoading, setOrdersLoading] = useState({});
  const [ordersModalUser, setOrdersModalUser] = useState(null);

  const PERMISSIONS = [
    { key: 'admin.access', label: 'Admin button & access' },
    { key: 'admin.users', label: 'Users' },
    { key: 'admin.roles', label: 'Roles' },
    { key: 'admin.products', label: 'Products' },
    { key: 'admin.orders', label: 'Orders' },
    { key: 'admin.reports', label: 'Reports' },
    { key: 'admin.pages', label: 'Pages' },
    { key: 'admin.pages.delete', label: 'Delete Pages' },
    { key: 'admin.pages.lock', label: 'Lock/Unlock Pages' },
    { key: 'admin.categories', label: 'Categories' },
    { key: 'admin.services', label: 'Services' },
    { key: 'admin.settings', label: 'Settings' },
    { key: 'admin.blogs', label: 'Blogs' },
    { key: 'admin.tools', label: 'Admin tools' },
    { key: 'admin.chat', label: 'Chat Support' },
    { key: 'admin.shops', label: 'Shops Locations' },
  ];

  const API_URL = `${String(API_BASE_URL || '').replace(/\/+$/, '')}/users`;
  const token = getToken();
  const { currency, rates } = useCurrency();

  const currencySymbol = (code) => {
    const c = String(code || '').toUpperCase();
    if (c === 'EUR') return '€';
    if (c === 'USD') return '$';
    if (c === 'ALL') return 'L';
    return c;
  };

  const formatMoney = (amountAll) => {
    const rate = Number(rates?.[currency] || 1);
    const display = rate > 0 ? Number(amountAll || 0) / rate : Number(amountAll || 0);
    return `${currencySymbol(currency)}${display.toFixed(2)} ${currency}`;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      if (!hasPermission('admin.users')) {
        navigate('/login');
        return;
      }
      if (!token) {
        setError("No token found. Please log in.");
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(API_URL, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data);
        // fetch server roles
        try {
          const rolesRes = await axios.get(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`);
          let serverRoles = rolesRes.data || [];
          // ensure any roles present on users are added to server
          const unique = Array.from(new Set((res.data || []).map(u => u.role || 'user')));
          for (const r of unique) {
            if (!serverRoles.includes(r)) {
              try {
                await axios.post(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`, { name: r }, { headers: { Authorization: `Bearer ${token}` } });
              } catch (_) { }
            }
          }
          // re-fetch roles after potential adds
          const rolesRefetch = await axios.get(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`);
          serverRoles = rolesRefetch.data || [];

          if (canManageRoles) {
            try {
              const adminRolesRes = await axios.get(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles/admin`, { headers: { Authorization: `Bearer ${token}` } });
              const adminRoles = adminRolesRes.data || [];
              setRoles(adminRoles.map(r => r.name));
              const map = {};
              adminRoles.forEach(r => { map[r.name] = Array.isArray(r.permissions) ? r.permissions : []; });
              setRolePermissions(map);
            } catch (_) {
              setRoles(serverRoles);
            }
          } else {
            setRoles(serverRoles);
          }
        } catch (err) {
          // fallback to roles derived from users
          const unique = Array.from(new Set((res.data || []).map(u => u.role || 'user')));
          setRoles(unique.length ? unique : ['user', 'admin']);
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          setError("Unauthorized. Please log in as admin.");
        } else {
          setError("Failed to fetch users");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token, API_URL, navigate, canManageRoles]);

  // keep roles in sync when users change elsewhere
  useEffect(() => {
    // keep server roles in sync locally (if users changed elsewhere, ensure server has those roles)
    const syncRoles = async () => {
      try {
        const rolesRes = await axios.get(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`);
        const serverRoles = rolesRes.data || [];
        const fromUsers = Array.from(new Set((users || []).map(u => u.role || 'user')));
        for (const r of fromUsers) {
          if (!serverRoles.includes(r)) {
            try { await axios.post(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`, { name: r }, { headers: { Authorization: `Bearer ${token}` } }); } catch (_) { }
          }
        }
        const refetch = await axios.get(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`);
        const nextRoles = refetch.data || [];
        if (canManageRoles) {
          try {
            const adminRolesRes = await axios.get(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles/admin`, { headers: { Authorization: `Bearer ${token}` } });
            const adminRoles = adminRolesRes.data || [];
            setRoles(adminRoles.map(r => r.name));
            const map = {};
            adminRoles.forEach(r => { map[r.name] = Array.isArray(r.permissions) ? r.permissions : []; });
            setRolePermissions(map);
          } catch (_) {
            setRoles(nextRoles);
          }
        } else {
          setRoles(nextRoles);
        }
      } catch (e) {
        // ignore
      }
    };
    syncRoles();
  }, [users, token, canManageRoles]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('name', newUser.name);
      fd.append('surname', newUser.surname || '');
      fd.append('username', newUser.username || '');
      fd.append('email', newUser.email);
      fd.append('password', newUser.password);
      fd.append('role', newUser.role || 'user');
      fd.append('phoneNumber', newUser.phoneNumber || '');
      fd.append('country', newUser.country || '');
      fd.append('stateProvince', newUser.stateProvince || '');
      fd.append('city', newUser.city || '');
      fd.append('zip', newUser.zip || '');
      fd.append('streetAddress', newUser.streetAddress || '');
      if (newUser.dateOfBirth) fd.append('dateOfBirth', newUser.dateOfBirth);
      if (newUser.gender) fd.append('gender', newUser.gender);
      fd.append('marketingEmail', newUser.marketingEmail);
      fd.append('marketingSMS', newUser.marketingSMS);
      fd.append('preferredCurrency', newUser.preferredCurrency || 'ALL');
      if (file) fd.append('profileImage', file);

      const res = await axios.post(
        API_URL,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers([...users, res.data]);
      setNewUser({ name: "", surname: "", email: "", password: "", role: "user", username: '', phoneNumber: '', country: '', stateProvince: '', city: '', zip: '', streetAddress: '', dateOfBirth: '', gender: '', marketingEmail: false, marketingSMS: false, preferredCurrency: 'ALL' });
      setFile(null);
      toast.success('Përdoruesi u krijua me sukses');
    } catch (err) {
      toast.error(err?.response?.data?.message || "Gabim gjatë krijimit të përdoruesit");
    }
  };

  const startEdit = (user) => {
    setEditId(user.id);
    setEditForm({
      name: user.name || '',
      surname: user.surname || '',
      email: user.email || '',
      role: user.role || 'user',
      password: "",
      username: user.username || '',
      phoneNumber: user.phoneNumber || '',
      streetAddress: user.streetAddress || '',
      city: user.city || '',
      stateProvince: user.stateProvince || '',
      zip: user.zip || '',
      country: user.country || '',
      dateOfBirth: user.dateOfBirth || '',
      gender: user.gender || '',
      marketingEmail: !!user.marketingEmail,
      marketingSMS: !!user.marketingSMS,
      preferredCurrency: user.preferredCurrency || 'ALL'
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({ name: "", surname: "", email: "", role: "user", password: "" });
  };

  const saveEdit = async (id) => {
    try {
      const payload = {
        name: editForm.name,
        surname: editForm.surname,
        email: editForm.email,
        role: editForm.role,
        username: editForm.username,
        phoneNumber: editForm.phoneNumber,
        streetAddress: editForm.streetAddress,
        city: editForm.city,
        stateProvince: editForm.stateProvince,
        zip: editForm.zip,
        country: editForm.country,
        dateOfBirth: editForm.dateOfBirth,
        gender: editForm.gender,
        marketingEmail: editForm.marketingEmail,
        marketingSMS: editForm.marketingSMS,
        preferredCurrency: editForm.preferredCurrency
      };
      if (editForm.password) payload.password = editForm.password;
      const res = await axios.put(
        `${API_URL}/${id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(users.map(u => (u.id === id ? res.data : u)));
      toast.success('Përdoruesi u përditësua me sukses');
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Gabim gjatë ruajtjes së përdoruesit");
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await axios.put(
        `${API_URL}/${id}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(
        users.map((user) =>
          user.id === id ? { ...user, role: newRole } : user
        )
      );
      toast.success('Roli u përditësua');
    } catch (err) {
      toast.error("Gabim gjatë përditësimit të rolit");
    }
  };

  const loadUserOrders = async (userId) => {
    if (ordersLoading[userId]) return;
    setOrdersLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.get(`${API_URL}/${userId}/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrdersByUser(prev => ({ ...prev, [userId]: Array.isArray(res.data) ? res.data : [] }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load orders');
      setOrdersByUser(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setOrdersLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const openOrdersModal = (user) => {
    setOrdersModalUser(user);
    if (!ordersByUser[user.id]) loadUserOrders(user.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(users.filter((user) => user.id !== id));
      toast.success('Përdoruesi u fshi');
    } catch (err) {
      toast.error("Gabim gjatë fshirjes së përdoruesit");
    }
  };

  if (loading) return <div className="admin-text" style={{ padding: 24 }}>Loading users...</div>;
  if (error) return <div className="admin-text" style={{ padding: 24, color: '#ef4444' }}>{error}</div>;

  return (
    <div>


      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="admin-card" style={{ width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', margin: 0 }}>
            {/* sticky header so close button stays visible */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>Add User</h3>
              <button onClick={() => { setShowAddModal(false); setNewUser({ name: '', surname: '', email: '', password: '', role: 'user' }); setFile(null); }}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>
                &times;
              </button>
            </div>
            <form onSubmit={async (e) => { await handleAddUser(e); setShowAddModal(false); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">Username</label>
                  <input className="admin-input" placeholder="Username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Role</label>
                  <select className="admin-select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                    {((roles && roles.length) ? roles : ['user', 'admin']).map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">First Name</label>
                  <input className="admin-input" placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Last Name</label>
                  <input className="admin-input" placeholder="Surname" value={newUser.surname} onChange={(e) => setNewUser({ ...newUser, surname: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">Email</label>
                  <input className="admin-input" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Password</label>
                  <input className="admin-input" placeholder="Password" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Phone</label>
                <input className="admin-input" placeholder="Phone (+country code)" value={newUser.phoneNumber} onChange={(e) => setNewUser({ ...newUser, phoneNumber: e.target.value })} />
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Address</label>
                <input className="admin-input" placeholder="Street address" value={newUser.streetAddress} onChange={(e) => setNewUser({ ...newUser, streetAddress: e.target.value })} style={{ marginBottom: 8 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <input className="admin-input" placeholder="City" value={newUser.city} onChange={(e) => setNewUser({ ...newUser, city: e.target.value })} />
                  <input className="admin-input" placeholder="State/Province" value={newUser.stateProvince} onChange={(e) => setNewUser({ ...newUser, stateProvince: e.target.value })} />
                  <input className="admin-input" placeholder="ZIP" value={newUser.zip} onChange={(e) => setNewUser({ ...newUser, zip: e.target.value })} />
                </div>
                <input className="admin-input" placeholder="Country" value={newUser.country} onChange={(e) => setNewUser({ ...newUser, country: e.target.value })} style={{ marginTop: 8 }} />
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Profile Picture (optional)</label>
                <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ display: 'block', fontSize: 13, color: '#64748b' }} />
              </div>

              <div className="admin-form-group" style={{ display: 'flex', gap: 24 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                  <input type="checkbox" checked={newUser.marketingEmail} onChange={(e) => setNewUser({ ...newUser, marketingEmail: e.target.checked })} /> Marketing email
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                  <input type="checkbox" checked={newUser.marketingSMS} onChange={(e) => setNewUser({ ...newUser, marketingSMS: e.target.checked })} /> Marketing SMS
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Preferred Currency</label>
                <select className="admin-select" value={newUser.preferredCurrency} onChange={(e) => setNewUser({ ...newUser, preferredCurrency: e.target.value })}>
                  <option value="ALL">ALL</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div className="admin-actions">
                <button type="button" onClick={() => { setShowAddModal(false); setNewUser({ name: '', surname: '', email: '', password: '', role: 'user', username: '', phoneNumber: '', country: '', stateProvince: '', city: '', zip: '', streetAddress: '', dateOfBirth: '', gender: '', marketingEmail: false, marketingSMS: false, preferredCurrency: 'ALL' }); setFile(null); }} className="admin-btn admin-btn-secondary">Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roles management modal */}
      {showRolesModal && canManageRoles ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="admin-card" style={{ width: '90%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>Manage Roles</h3>
              <button onClick={() => { setShowRolesModal(false); setRoleEditIndex(-1); setRoleEditValue(''); setRoleEditPerms([]); setNewRoleName(''); setNewRolePerms([]); }}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>
                &times;
              </button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, fontSize: 14, textTransform: 'uppercase', color: '#64748b' }}>Existing Roles</h4>
                {roles.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>No roles found.</div> : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {roles.map((r, idx) => (
                      <div key={r} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: 8, background: '#fff', borderRadius: 4, border: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {roleEditIndex === idx ? (
                              <input className="admin-input" style={{ padding: '4px 8px', height: 32 }} value={roleEditValue} onChange={(e) => setRoleEditValue(e.target.value)} />
                            ) : (
                              <div style={{ fontWeight: 600, textTransform: 'capitalize', fontSize: 14 }}>{r}</div>
                            )}
                            <div style={{ color: '#94a3b8', fontSize: 12 }}>
                              {users.filter(u => (u.role || 'user') === r).length} users
                            </div>
                          </div>
                          {roleEditIndex === idx ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                              {PERMISSIONS.map(p => (
                                <label key={p.key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#475569' }}>
                                  <input
                                    type="checkbox"
                                    checked={roleEditPerms.includes(p.key)}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setRoleEditPerms(prev => checked ? Array.from(new Set([...prev, p.key])) : prev.filter(x => x !== p.key));
                                    }}
                                  />
                                  {p.label}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div style={{ color: '#94a3b8', fontSize: 12 }}>
                              {(rolePermissions[r] || []).length ? (rolePermissions[r] || []).map(p => PERMISSIONS.find(x => x.key === p)?.label || p).join(', ') : 'No permissions'}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', minWidth: 160 }}>
                          {roleEditIndex === idx ? (
                            <>
                              <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={async () => {
                                const old = roles[idx];
                                const renamed = roleEditValue.trim();
                                if (!renamed) return toast.error('Emri i rolit nuk mund të jetë bosh');
                                const affected = users.filter(u => (u.role || 'user') === old);
                                for (const a of affected) {
                                  try { await handleRoleChange(a.id, renamed); } catch (err) { console.error(err); }
                                }
                                try {
                                  const resp = await axios.put(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles/${encodeURIComponent(old)}`, { name: renamed, permissions: roleEditPerms }, { headers: { Authorization: `Bearer ${token}` } });
                                  setRoles(resp.data || []);
                                } catch (err) {
                                  console.error(err);
                                  setRoles((prev) => (prev || []).map(r => (r === old ? renamed : r)));
                                }
                                setRolePermissions(prev => {
                                  const next = { ...prev };
                                  delete next[old];
                                  next[renamed] = roleEditPerms;
                                  return next;
                                });
                                setUsers(users.map(u => (u.role === old ? { ...u, role: renamed } : u)));
                                setRoleEditIndex(-1);
                                setRoleEditValue('');
                                setRoleEditPerms([]);
                              }} style={{ padding: '6px 10px', fontWeight: 600 }}>Save</button>
                              <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => { setRoleEditIndex(-1); setRoleEditValue(''); setRoleEditPerms([]); }} style={{ padding: '6px 10px', fontWeight: 600 }}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => { setRoleEditIndex(idx); setRoleEditValue(r); setRoleEditPerms(rolePermissions[r] || []); }} style={{ padding: '6px 10px', fontWeight: 600 }}>Edit</button>
                              <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={async () => {
                                if (!window.confirm(`Delete role "${r}"? Affected users will become 'user'.`)) return;
                                const affected = users.filter(u => (u.role || 'user') === r);
                                for (const a of affected) {
                                  try { await handleRoleChange(a.id, 'user'); } catch (err) { console.error(err); }
                                }
                                try {
                                  const resp = await axios.delete(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles/${encodeURIComponent(r)}`, { headers: { Authorization: `Bearer ${token}` } });
                                  setRoles(resp.data || []);
                                } catch (err) {
                                  console.error(err);
                                  setRoles(roles.filter(role => role !== r));
                                }
                                setRolePermissions(prev => {
                                  const next = { ...prev };
                                  delete next[r];
                                  return next;
                                });
                                setUsers(users.map(u => ((u.role || 'user') === r ? { ...u, role: 'user' } : u)));
                              }} style={{ padding: '6px 10px', fontWeight: 600 }}>Delete</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ padding: 12, borderRadius: 8, border: '1px dashed #cbd5e1' }}>
                <h4 style={{ marginTop: 0, fontSize: 14, color: '#64748b' }}>Add New Role</h4>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="admin-input" placeholder="Role name" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
                  <button onClick={async () => {
                    const name = newRoleName.trim();
                    if (!name) return toast.error('Emri i rolit nuk mund të jetë bosh');
                    if (roles.includes(name)) return toast.error('Ky rol ekziston tashmë');
                    try {
                      const resp = await axios.post(`${String(API_BASE_URL || '').replace(/\/+$/, '')}/roles`, { name, permissions: newRolePerms }, { headers: { Authorization: `Bearer ${token}` } });
                      setRoles(resp.data || []);
                      setRolePermissions(prev => ({ ...prev, [name]: newRolePerms }));
                      setNewRoleName('');
                      setNewRolePerms([]);
                      toast.success('Roli u shtua');
                    } catch (err) {
                      toast.error(err?.response?.data?.message || 'Dështoi shtimi i rolit');
                    }
                  }} className="admin-btn admin-btn-primary">Add</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                  {PERMISSIONS.map(p => (
                    <label key={p.key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#475569' }}>
                      <input
                        type="checkbox"
                        checked={newRolePerms.includes(p.key)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewRolePerms(prev => checked ? Array.from(new Set([...prev, p.key])) : prev.filter(x => x !== p.key));
                        }}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button onClick={() => setShowAddModal(true)} className="admin-btn admin-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Add User
          </button>
          {canManageRoles ? (
            <button onClick={() => setShowRolesModal(true)} className="admin-btn admin-btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
              Manage Roles
            </button>
          ) : null}
        </div>
        <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <th style={{ padding: '16px 20px', fontWeight: 600 }}>User</th>
              <th style={{ padding: '16px 20px', fontWeight: 600 }}>Email</th>
              <th style={{ padding: '16px 20px', fontWeight: 600 }}>Role</th>
              <th style={{ padding: '16px 20px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #e2e8f0', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <td style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', background: '#e2e8f0', flexShrink: 0 }}>
                      {user.profileImage ? (
                        <Link to={`/users/${user.id}`} style={{ display: 'block', width: '100%', height: '100%' }}>
                          <img src={resolveAssetUrl(user.profileImage)} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Link>
                      ) : (
                        <Link to={`/users/${user.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', textDecoration: 'none' }}>
                          <div style={{ fontWeight: 600, color: '#64748b', fontSize: 16 }}>{(user.name || '').charAt(0).toUpperCase() || '?'}</div>
                        </Link>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>
                        <Link to={`/users/${user.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{user.name} {user.surname}</Link>
                      </div>
                      {user.username && <div style={{ fontSize: 12, color: '#64748b' }}>@{user.username}</div>}
                    </div>
                  </div>
                </td>
                <td style={{ padding: '16px 20px', color: '#64748b', fontSize: 14 }}>
                  {user.email}
                </td>
                <td style={{ padding: '16px 20px' }}>
                  <span className="admin-badge neutral" style={{ textTransform: 'capitalize' }}>{user.role}</span>
                </td>
                <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                  {editId !== user.id && (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button
                        onClick={() => openOrdersModal(user)}
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                        </svg>
                        Orders
                      </button>
                      <button onClick={() => startEdit(user)} className="admin-btn admin-btn-secondary admin-btn-sm" style={{ padding: '6px' }} title="Edit User">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button onClick={() => handleDelete(user.id)} className="admin-btn admin-btn-danger admin-btn-sm" style={{ padding: '6px' }} title="Delete User">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {editId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="admin-card" style={{ width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>Edit User</h3>
              <button onClick={cancelEdit}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>
                &times;
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await saveEdit(editId); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">Username</label>
                  <input className="admin-input" placeholder="Username" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Role</label>
                  <select className="admin-select" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                    {((roles && roles.length) ? roles : ['user', 'admin']).map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">First Name</label>
                  <input className="admin-input" placeholder="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Last Name</label>
                  <input className="admin-input" placeholder="Surname" value={editForm.surname} onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">Email</label>
                  <input className="admin-input" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">New Password (leave blank to keep current)</label>
                  <input className="admin-input" placeholder="New Password" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Phone</label>
                <input className="admin-input" placeholder="Phone (+country code)" value={editForm.phoneNumber} onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })} />
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Address</label>
                <input className="admin-input" placeholder="Street address" value={editForm.streetAddress} onChange={(e) => setEditForm({ ...editForm, streetAddress: e.target.value })} style={{ marginBottom: 8 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <input className="admin-input" placeholder="City" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                  <input className="admin-input" placeholder="State/Province" value={editForm.stateProvince} onChange={(e) => setEditForm({ ...editForm, stateProvince: e.target.value })} />
                  <input className="admin-input" placeholder="ZIP" value={editForm.zip} onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })} />
                </div>
                <input className="admin-input" placeholder="Country" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} style={{ marginTop: 8 }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div className="admin-form-group">
                  <label className="admin-label">Date of Birth</label>
                  <input className="admin-input" type="date" value={editForm.dateOfBirth ? String(editForm.dateOfBirth).split('T')[0] : ''} onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Gender</label>
                  <select className="admin-select" value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}>
                    <option value="">Prefer not to say</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-group" style={{ display: 'flex', gap: 24, marginTop: 16 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                  <input type="checkbox" checked={editForm.marketingEmail} onChange={(e) => setEditForm({ ...editForm, marketingEmail: e.target.checked })} /> Marketing email
                </label>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                  <input type="checkbox" checked={editForm.marketingSMS} onChange={(e) => setEditForm({ ...editForm, marketingSMS: e.target.checked })} /> Marketing SMS
                </label>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Preferred Currency</label>
                <select className="admin-select" value={editForm.preferredCurrency} onChange={(e) => setEditForm({ ...editForm, preferredCurrency: e.target.value })}>
                  <option value="ALL">ALL</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div className="admin-actions" style={{ marginTop: 24 }}>
                <button type="button" onClick={cancelEdit} className="admin-btn admin-btn-secondary">Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {ordersModalUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ width: 'min(900px, 92vw)', maxHeight: '85vh', overflow: 'auto', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
              <div style={{ fontWeight: 700 }}>Orders for {ordersModalUser.name || 'User'} {ordersModalUser.surname || ''}</div>
              <button onClick={() => setOrdersModalUser(null)} className="admin-btn admin-btn-secondary admin-btn-sm">Close</button>
            </div>
            <div style={{ padding: 16 }}>
              {ordersLoading[ordersModalUser.id] ? (
                <div style={{ fontSize: 12, color: '#64748b' }}>Loading orders...</div>
              ) : (ordersByUser[ordersModalUser.id] && ordersByUser[ordersModalUser.id].length > 0) ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {ordersByUser[ordersModalUser.id].map((o) => (
                    <div key={o.id} style={{ border: '1px solid #eef2f7', borderRadius: 12, padding: 12, background: '#fbfdff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <div><strong>Order #{o.id}</strong></div>
                        <div>{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '—'}</div>
                      </div>
                      <div style={{ marginTop: 6 }}>Status: <strong>{o.status || '—'}</strong> · Total: <strong>{formatMoney(o.total)}</strong></div>
                      <div style={{ marginTop: 4 }}>Shipping: <strong>{[o.address, o.city, o.region, o.zip, o.country].filter(Boolean).join(', ') || '—'}</strong></div>
                      {Array.isArray(o.items) && o.items.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ color: '#64748b', marginBottom: 4 }}>Items:</div>
                          <div style={{ display: 'grid', gap: 4 }}>
                            {o.items.map((it) => {
                              const img = it.product?.image || (Array.isArray(it.product?.images) ? it.product.images[0] : '') || it.image || '';
                              const imgUrl = img ? resolveAssetUrl(img) : '';
                              return (
                                <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ width: 28, height: 28, borderRadius: 6, background: '#f1f5f9', overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {imgUrl ? (
                                        <img src={imgUrl} alt={it.product?.name || 'Item'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      ) : null}
                                    </span>
                                    <span>{it.product?.name || it.productName || 'Item'}</span>
                                  </span>
                                  <span>x{it.quantity} · {formatMoney(it.price)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px dashed #e2e8f0', marginTop: 4 }}>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>Total spent</div>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>
                      {formatMoney(
                        ordersByUser[ordersModalUser.id].reduce((sum, o) => {
                          const items = Array.isArray(o.items) ? o.items : [];
                          const orderSum = items.reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 0), 0);
                          return sum + orderSum;
                        }, 0)
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#64748b' }}>No orders found.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
